import type { RowType } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/types'
import type { PricingPlan } from 'Services/ProductManagement/Features/Product/types'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { calculateISODurationLengthInMinutes } from 'Util/formatters'
import type { GridColDef } from '@mui/x-data-grid'

interface CreateRowsOptions {
  zoneDefinition: ZoneDefinition
  columns: GridColDef[]
  isCellDisabledByDefault: boolean
}

/**
 * Creates empty grid rows for each zone definition.
 * Row id is the first zone in the zoneMap (the starting zone).
 *
 * The zoneDefinition.zones consists of all the possible zone combinations (zoneMaps).
 * Each zoneMap is an array of zoneIds, such as ['20', '21', '22'].
 */
export const createRows = ({
  zoneDefinition,
  columns,
  isCellDisabledByDefault
}: CreateRowsOptions): RowType[] => {
  const newRows: RowType[] = []

  for (const zoneMap of zoneDefinition.zones) {
    const currentZone = zoneMap[0]
    const existingRow = newRows.find(row => row.id === currentZone)
    const row: RowType = existingRow ?? { id: '', title: '' }
    row.id = currentZone
    row.title = columns.find(column => column.field === currentZone)?.headerName ?? ''
    const lastZone = zoneMap.at(-1)
    // Cells are empty by default. Dependent tab cells are disabled by default.
    if (lastZone !== undefined) {
      row[lastZone] = isCellDisabledByDefault ? undefined : ''
    }

    if (!existingRow) {
      newRows.push(row)
    }
  }

  return newRows
}

/**
 * Populates grid rows with existing pricing values from pricing plans.
 *
 * In HSL: all pricing plans are shown.
 * In Waltti: only enables cells for zone maps that have a usage_validity_duration set
 * (exchange period drives pricing availability).
 */
export const addExistingPricings = (newRows: RowType[], pricingPlans: PricingPlan[]): RowType[] => {
  pricingPlans.forEach(pricingPlan => {
    const row = newRows.find(row => row.id === pricingPlan.zone_map[0])
    if (!row) return
    const targetZone = pricingPlan.zone_map.at(-1)

    // TODO: Disable cell if no usage_validity_duration in Waltti when SB has support for creating
    // pricing plans with empty pricings array.
    if (targetZone === undefined) return
    row[targetZone] = pricingPlan.pricings[0]?.price ?? ''
  })

  return newRows
}

/**
 * Populates grid rows with existing exchange period values from pricing plans.
 */
export const addExistingExchangePeriods = (
  newRows: RowType[],
  pricingPlans: PricingPlan[]
): RowType[] => {
  pricingPlans.forEach(pricingPlan => {
    const row = newRows.find(row => row.id === pricingPlan.zone_map[0])
    if (!row) return
    const targetZone = pricingPlan.zone_map.at(-1)
    if (targetZone === undefined) return

    if (pricingPlan.usage_validity_duration) {
      row[targetZone] = calculateISODurationLengthInMinutes(pricingPlan.usage_validity_duration)
    } else {
      row[targetZone] = ''
    }
  })

  return newRows
}
